export const PLATFORM_ROLES = {
  ADMIN: 'platform_admin',
  SUPPORT: 'platform_support',
  READONLY: 'platform_readonly',
} as const;

export type PlatformRole = (typeof PLATFORM_ROLES)[keyof typeof PLATFORM_ROLES];

export const PLATFORM_PERMISSIONS = {
  SHOPS_READ: 'shops.read',
  SHOPS_UPDATE: 'shops.update',
  SHOPS_SUSPEND: 'shops.suspend',
  SHOPS_RESTORE: 'shops.restore',
  SHOPS_VERIFY: 'shops.verify',
  USERS_READ: 'users.read',
  USERS_SUSPEND: 'users.suspend',
  USERS_RESTORE: 'users.restore',
  MEMBERS_READ: 'members.read',
  MEMBERS_REMOVE: 'members.remove',
  BILLING_READ: 'billing.read',
  BILLING_MANAGE: 'billing.manage',
  REVIEWS_MODERATE: 'reviews.moderate',
  GALLERY_MODERATE: 'gallery.moderate',
  SUPPORT_READ: 'support.read',
  SUPPORT_MANAGE: 'support.manage',
  AUDIT_READ: 'audit.read',
  AUDIT_VIEW: 'audit.view',
  AUDIT_VIEW_SECURITY: 'audit.view_security',
  AUDIT_VIEW_FINANCIAL: 'audit.view_financial',
  SUSPENSION_VIEW: 'suspension.view',
  SUSPENSION_CREATE: 'suspension.create',
  SUSPENSION_REMOVE: 'suspension.remove',
  ABUSE_VIEW: 'abuse.view',
  ABUSE_INVESTIGATE: 'abuse.investigate',
  ABUSE_ENFORCE: 'abuse.enforce',
  BILLING_VIEW: 'billing.view',
  BILLING_VIEW_PROVIDER_REFERENCE: 'billing.view_provider_reference',
  REFUND_VIEW: 'refund.view',
  REFUND_REQUEST: 'refund.request',
  REFUND_APPROVE: 'refund.approve',
  SUPPORT_VIEW_USERS: 'support.view_users',
  SUPPORT_VIEW_SHOPS: 'support.view_shops',
  SUPPORT_VIEW_MEMBERS: 'support.view_members',
  SECURITY_REVOKE_SESSIONS: 'security.revoke_sessions',
  SUPPORT_SEARCH: 'support.search',
  SUPPORT_VIEW_AUTH: 'support.view_auth',
  SUPPORT_VIEW_SESSIONS: 'support.view_sessions',
  SUPPORT_VIEW_AUDIT: 'support.view_audit',
  SUPPORT_VIEW_PAYMENTS: 'support.view_payments',
  SUPPORT_VIEW_NOTIFICATIONS: 'support.view_notifications',
  SUPPORT_RESEND_INVITATION: 'support.resend_invitation',
  SUPPORT_RESEND_NOTIFICATION: 'support.resend_notification',
  SUPPORT_REVOKE_SESSION: 'support.revoke_session',
  SUPPORT_DISABLE_MEMBERSHIP: 'support.disable_membership',
  SUPPORT_REACTIVATE_MEMBERSHIP: 'support.reactivate_membership',
  SUPPORT_FIX_PRIMARY_SHOP: 'support.fix_primary_shop',
  SUPPORT_LINK_ACCOUNT_REVIEW: 'support.link_account_review',
  SUPPORT_RETRY_OPERATION: 'support.retry_operation',
  SUPPORT_EXPORT_DIAGNOSTICS: 'support.export_diagnostics',
  SUPPORT_MANAGE_ROLES: 'support.manage_support_roles',
  ADMIN_MANAGE: 'admin.manage',
  ADMIN_PERMISSIONS_MANAGE: 'admin.permissions.manage',
} as const;

export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[keyof typeof PLATFORM_PERMISSIONS];

export const PLATFORM_ROLE_PERMISSIONS: Record<PlatformRole, readonly PlatformPermission[]> = {
  [PLATFORM_ROLES.ADMIN]: Object.values(PLATFORM_PERMISSIONS),
  [PLATFORM_ROLES.SUPPORT]: [
    PLATFORM_PERMISSIONS.SHOPS_READ,
    PLATFORM_PERMISSIONS.USERS_READ,
    PLATFORM_PERMISSIONS.MEMBERS_READ,
    PLATFORM_PERMISSIONS.SUPPORT_READ,
    PLATFORM_PERMISSIONS.SUPPORT_MANAGE,
    PLATFORM_PERMISSIONS.AUDIT_READ,
    PLATFORM_PERMISSIONS.AUDIT_VIEW,
    PLATFORM_PERMISSIONS.SUSPENSION_VIEW,
    PLATFORM_PERMISSIONS.ABUSE_VIEW,
    PLATFORM_PERMISSIONS.BILLING_VIEW,
    PLATFORM_PERMISSIONS.REFUND_VIEW,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_USERS,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_SHOPS,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_MEMBERS,
    PLATFORM_PERMISSIONS.SUPPORT_SEARCH,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_AUTH,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_SESSIONS,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_AUDIT,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_PAYMENTS,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_NOTIFICATIONS,
    PLATFORM_PERMISSIONS.SUPPORT_RESEND_INVITATION,
    PLATFORM_PERMISSIONS.SUPPORT_RESEND_NOTIFICATION,
    PLATFORM_PERMISSIONS.SUPPORT_REVOKE_SESSION,
    PLATFORM_PERMISSIONS.SUPPORT_DISABLE_MEMBERSHIP,
    PLATFORM_PERMISSIONS.SUPPORT_REACTIVATE_MEMBERSHIP,
    PLATFORM_PERMISSIONS.SUPPORT_FIX_PRIMARY_SHOP,
    PLATFORM_PERMISSIONS.SUPPORT_LINK_ACCOUNT_REVIEW,
    PLATFORM_PERMISSIONS.SUPPORT_RETRY_OPERATION,
    PLATFORM_PERMISSIONS.SUPPORT_EXPORT_DIAGNOSTICS,
    PLATFORM_PERMISSIONS.SUPPORT_MANAGE_ROLES,
  ],
  [PLATFORM_ROLES.READONLY]: [
    PLATFORM_PERMISSIONS.SHOPS_READ,
    PLATFORM_PERMISSIONS.USERS_READ,
    PLATFORM_PERMISSIONS.MEMBERS_READ,
    PLATFORM_PERMISSIONS.BILLING_READ,
    PLATFORM_PERMISSIONS.SUPPORT_READ,
    PLATFORM_PERMISSIONS.AUDIT_READ,
    PLATFORM_PERMISSIONS.AUDIT_VIEW,
    PLATFORM_PERMISSIONS.BILLING_VIEW,
    PLATFORM_PERMISSIONS.REFUND_VIEW,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_USERS,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_SHOPS,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_MEMBERS,
    PLATFORM_PERMISSIONS.SUPPORT_SEARCH,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_AUTH,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_SESSIONS,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_AUDIT,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_PAYMENTS,
    PLATFORM_PERMISSIONS.SUPPORT_VIEW_NOTIFICATIONS,
  ],
};
